import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type {
  InteriorFloorMaterialOverride,
  ExteriorFloorMaterialOverride,
} from "building-types/settings/floorMaterialSettings";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { Face } from "geom/brep";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";

/**
 * Base operator types for floor assemblies
 * @TODO - these should be merged with other floor assembly types
 */

export type FloorAssemblyOperationType = "ADD" | "REPLACE_ALL";

interface FloorAssemblyOperatorBase<T extends FloorAssemblyOperationType> {
  readonly operationType: T;
}

/**
 * Finish specific types
 */

export enum FloorFinishType {
  INTERIOR = "INTERIOR",
  EXTERIOR = "EXTERIOR",
  NONE = "NONE",
  DYNAMIC = "DYNAMIC",
}

/**
 * User defined finishes, usually hosted to a room
 */
export type FloorAssemblyFinishLayerOverride = FloorAssemblyFinishLayerOperator &
  FormulaDisabledDocEntityBase<"FLOOR_FINISH"> & {
    readonly id: PrimaryEntityId<"FLOOR_FINISH">;
    readonly parentId?: DocEntityParentId<"ROOM">;
  };

/**
 * Floor finish operations that can affect a finish assembly
 */
export type FloorAssemblyFinishLayerOperator =
  | FloorAssemblyInteriorFinishLayerOperator
  | FloorAssemblyExteriorFinishLayerOperator
  | FloorAssemblyDynamicFinishLayerOperator;

interface FloorAssemblyFinishLayerOperatorBase<T extends FloorFinishType>
  extends FloorAssemblyOperatorBase<FloorAssemblyOperationType> {
  readonly finishType: T;
  readonly finishPerimeter?: PolyLine2;
  readonly isFloorThreshold?: boolean;
}

export interface SourceEntityData {
  /** The entity this finish layer came from: the FLOOR_FINISH override, or the casework or prop
   * whose floor material replaced it. Carried so the drawn face can be picked as that entity. */
  readonly srcHandle?: DocEntityHandle;
}

/**
 * An operator annotated with the entity generation derived it from.
 *
 * Kept out of the operator interfaces themselves because those double as the FLOOR_FINISH doc
 * entity: a `DocEntityHandle` declared there would enrol FLOOR_FINISH in reference integrity
 * against every entity a handle can name, for a field no document ever writes.
 */
export type SourcedFloorAssemblyFinishLayerOperator = FloorAssemblyFinishLayerOperator & SourceEntityData;

export interface FloorAssemblyInteriorFinishLayerOperator
  extends FloorAssemblyFinishLayerOperatorBase<FloorFinishType.INTERIOR> {
  readonly finishMaterial: InteriorFloorMaterialOverride;
}

export interface FloorAssemblyExteriorFinishLayerOperator
  extends FloorAssemblyFinishLayerOperatorBase<FloorFinishType.EXTERIOR> {
  readonly finishMaterial: ExteriorFloorMaterialOverride;
}

export interface FloorAssemblyDynamicFinishLayerOperator
  extends FloorAssemblyFinishLayerOperatorBase<FloorFinishType.DYNAMIC> {
  // DynamicFLoorFinishAssembly derives all of it's properties from settings
}

/**
 * Resolved combined assembly layers which can come from various combined places / operators
 */
export type FloorAssemblyFinishLayer = FloorAssemblyInteriorFinishLayer | FloorAssemblyExteriorFinishLayer;

interface FloorAssemblyFinishLayerBase<T extends FloorFinishType> {
  readonly finishType: T;
  readonly outlines: PolyLine2Polygon[];
  readonly srcHandle?: DocEntityHandle;
  readonly isFloorThreshold?: boolean;
}

export interface FloorAssemblyExteriorFinishLayer extends FloorAssemblyFinishLayerBase<FloorFinishType.EXTERIOR> {
  readonly material: ExteriorFloorMaterialOverride;
}

export interface FloorAssemblyInteriorFinishLayer extends FloorAssemblyFinishLayerBase<FloorFinishType.INTERIOR> {
  readonly material: InteriorFloorMaterialOverride;
}

/**
 * Resolved Floor Finish object, with face and material slot
 */
export interface FloorFinish {
  readonly finishType: FloorFinishType.INTERIOR | FloorFinishType.EXTERIOR;
  readonly finishFace: Face; // eventually this should be an extrusion
  readonly materialSlot: PaletteMaterialSlot;
  readonly materialSrc: MaterialSrc;
  readonly isFloorTheshold?: boolean;
  readonly srcHandle?: DocEntityHandle;
}
